import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomSelect, Input, StatCard, Amount, MonthlyBarChart, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton } from '@redpanda/ui';
import { isAdmin, toIntlLocale, useRefetchOnFocus, createKeyedCache, useRememberedState } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { getAttendanceReport, getPayrollReport, getLaborInsuranceReport, getHRCostReport } from '../services/api';
import { useDataChanged } from '../utils/dataEvents';
import { thisMonthRange } from '../utils/attendance';
import { monthOptions, yearOptions } from '../utils/period';

type Tab = 'payroll' | 'attendance' | 'insurance' | 'cost';
const ADMIN_TABS: Tab[] = ['payroll', 'attendance', 'insurance', 'cost'];
// 薪資類報表只給管理員；一般使用者只看出勤統計
const USER_TABS: Tab[] = ['attendance'];

// 報表資料 persist（R40）：切頁、重新整理時先顯示上次的結果再背景更新；原本每次進頁都是一片空白、要按「查詢」
const usePayrollReport = createKeyedCache('hr-report-payroll', async (q: { year: number; month?: number }) => (await getPayrollReport(q.year, q.month)).data);
const useAttendanceReport = createKeyedCache('hr-report-attendance', async (q: { startDate: string; endDate: string }) => (await getAttendanceReport(q.startDate, q.endDate)).data);
const useInsuranceReport = createKeyedCache('hr-report-insurance-v2', async (q: { year: number; month?: number }) => (await getLaborInsuranceReport(q.year, q.month)).data);
const useCostReport = createKeyedCache('hr-report-cost', async (q: { startYear: number; endYear: number }) => (await getHRCostReport(q.startYear, q.endYear)).data);

const currentYear = new Date().getFullYear();
const years = yearOptions(5);

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className = 'md:w-36' }) => (
  <label className={`block ${className}`}>
    <span className="sr-only">{label}</span>
    {children}
  </label>
);

/** 一張報表的外框：條件改了就自動查（不用按「查詢」），沒抓過顯示骨架、沒有資料顯示空狀態 */
const ReportBody: React.FC<{ data: unknown; empty: boolean; emptyText: string; columns?: number; children: React.ReactNode }> = ({ data, empty, emptyText, columns = 4, children }) => {
  if (data === undefined) return <TableSkeleton rows={4} columns={columns} showHeader={true} />;
  if (empty) {
    return <div className="rounded-lg border border-line bg-surface px-6 py-12 text-center text-sm text-fg-secondary">{emptyText}</div>;
  }
  return <>{children}</>;
};

const tableBox = 'bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg';

const ReportPage: React.FC = () => {
  const { t, i18n } = useTranslation(['pages', 'translation']);
  const locale = toIntlLocale(i18n.language);
  const { user } = useContext(UserContext)!;
  const TABS = isAdmin(user?.role) ? ADMIN_TABS : USER_TABS;
  // 分頁存在網址（?tab=）；網址沒指定時（從側欄進來）顯示上次看的分頁（同 PM 應收帳款頁）
  // 記住時用全部分頁比對：使用者資料載入前 TABS 會先是一般使用者的清單，不能因此把管理員記住的分頁判成無效
  const [searchParams, setSearchParams] = useSearchParams();
  const [rememberedTab, rememberTab] = useRememberedState<Tab>('hr-ui:reports.tab', TABS[0], ADMIN_TABS);
  const tabParam = searchParams.get('tab') as Tab | null;
  const tab = tabParam && TABS.includes(tabParam) ? tabParam : TABS.includes(rememberedTab) ? rememberedTab : TABS[0];
  const setTab = (next: Tab) => {
    rememberTab(next);
    setSearchParams(next === TABS[0] ? {} : { tab: next }, { replace: true });
  };
  useEffect(() => { if (tabParam && TABS.includes(tabParam)) rememberTab(tabParam); }, [tabParam, TABS, rememberTab]); // 從連結帶 ?tab= 進來也算最後看的分頁

  // 條件（預設：本年、本月 1 日到今天、去年到今年）
  const [payrollYear, setPayrollYear] = useState(currentYear);
  const [payrollMonth, setPayrollMonth] = useState<number | undefined>();
  const [attendanceRange, setAttendanceRange] = useState(thisMonthRange);
  const [insuranceYear, setInsuranceYear] = useState(currentYear);
  const [insuranceMonth, setInsuranceMonth] = useState<number | undefined>();
  const [costStartYear, setCostStartYear] = useState(currentYear - 1);
  const [costEndYear, setCostEndYear] = useState(currentYear);

  const rangeValid = !!attendanceRange.startDate && !!attendanceRange.endDate && attendanceRange.startDate <= attendanceRange.endDate;
  const payroll = usePayrollReport(tab === 'payroll' ? { year: payrollYear, month: payrollMonth } : null);
  const attendance = useAttendanceReport(tab === 'attendance' && rangeValid ? attendanceRange : null);
  const insurance = useInsuranceReport(tab === 'insurance' ? { year: insuranceYear, month: insuranceMonth } : null);
  const cost = useCostReport(tab === 'cost' && costStartYear <= costEndYear ? { startYear: costStartYear, endYear: costEndYear } : null);
  const current = { payroll, attendance, insurance, cost }[tab];

  // 目前分頁的條件一變就查一次；薪資、出勤異動時重抓
  useEffect(() => {
    current.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.key]);
  useDataChanged(['payrolls', 'attendance', 'employees'], () => current.refetch());
  useRefetchOnFocus(() => current.refetch());

  const months = [{ value: '', label: t('pages:reports.fullYear') }, ...monthOptions(t)];
  const yearMonthFilters = (year: number, setYear: (y: number) => void, month: number | undefined, setMonth: (m: number | undefined) => void) => (
    <>
      <Field label={t('pages:reports.year')}>
        <CustomSelect value={String(year)} onChange={value => setYear(Number(value))} options={years} />
      </Field>
      <Field label={t('pages:reports.month')}>
        <CustomSelect value={month ? String(month) : ''} onChange={value => setMonth(value ? Number(value) : undefined)} options={months} />
      </Field>
    </>
  );

  const hours = (value: number) => t('pages:reports.attendance.hoursValue', { value: Math.round(value * 10) / 10 });
  const emptyText = t('pages:reports.noData');

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">{t('pages:reports.title')}</h1>

      {/* 分頁籤（底線式，存在網址 ?tab=，同 PM 專案詳情） */}
      <nav className="mb-6 flex gap-6 border-b border-line overflow-x-auto scrollbar-hide" aria-label={t('pages:reports.title')}>
        {TABS.map(key => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => setTab(key)}
            className={`-mb-px h-10 whitespace-nowrap border-b-2 text-sm font-medium transition-colors ${
              tab === key ? 'border-accent text-accent' : 'border-transparent text-fg-secondary hover:text-fg'
            }`}
          >
            {t(`pages:reports.tabs.${key}`)}
          </button>
        ))}
      </nav>

      {/* 條件列（控制項 h-10），改了就自動查詢 */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center">
        {tab === 'payroll' && yearMonthFilters(payrollYear, setPayrollYear, payrollMonth, setPayrollMonth)}
        {tab === 'insurance' && yearMonthFilters(insuranceYear, setInsuranceYear, insuranceMonth, setInsuranceMonth)}
        {tab === 'attendance' && (
          <>
            <Field label={t('pages:reports.attendance.startDate')} className="md:w-44">
              <Input type="date" value={attendanceRange.startDate} max={attendanceRange.endDate} onChange={e => setAttendanceRange(r => ({ ...r, startDate: e.target.value }))} />
            </Field>
            <span className="hidden md:inline text-fg-secondary">–</span>
            <Field label={t('pages:reports.attendance.endDate')} className="md:w-44">
              <Input type="date" value={attendanceRange.endDate} min={attendanceRange.startDate} onChange={e => setAttendanceRange(r => ({ ...r, endDate: e.target.value }))} />
            </Field>
          </>
        )}
        {tab === 'cost' && (
          <>
            <Field label={t('pages:reports.cost.startYear')}>
              <CustomSelect value={String(costStartYear)} onChange={value => setCostStartYear(Number(value))} options={years} />
            </Field>
            <span className="hidden md:inline text-fg-secondary">–</span>
            <Field label={t('pages:reports.cost.endYear')}>
              <CustomSelect value={String(costEndYear)} onChange={value => setCostEndYear(Number(value))} options={years} />
            </Field>
          </>
        )}
      </div>

      {tab === 'payroll' && (
        <ReportBody data={payroll.data} empty={!payroll.data?.summary.totalRecords} emptyText={emptyText}>
          {payroll.data && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard title={t('pages:reports.payroll.payrollRecordCount')} value={payroll.data.summary.totalRecords.toLocaleString(locale)} />
                <StatCard title={t('pages:reports.payroll.totalGrossPay')} value={<Amount value={payroll.data.summary.totalGrossPay} />} />
                <StatCard title={t('pages:reports.payroll.totalDeductions')} value={<Amount value={payroll.data.summary.totalDeductions} />} />
                <StatCard title={t('pages:reports.payroll.totalNetSalary')} value={<Amount value={payroll.data.summary.totalNetSalary} />} />
              </div>
              <section>
                <h2 className="text-base font-semibold text-fg mb-3">{t('pages:reports.payroll.departmentStats')}</h2>
                <div className={tableBox}>
                  <div className="overflow-x-auto">
                    <Table style={{ tableLayout: 'fixed', minWidth: '560px' }}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('translation:common.table.columns.department')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.payroll.employeeCount')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.payroll.totalBaseSalary')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.payroll.totalNetSalary')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payroll.data.departmentStats.map((dept: any) => (
                          <TableRow key={dept.department || '-'}>
                            <TableCell className="font-medium text-fg">{dept.department || t('pages:reports.noDepartment')}</TableCell>
                            <TableCell numeric>{dept.employeeCount}</TableCell>
                            <TableCell numeric><Amount value={dept.totalBaseSalary} /></TableCell>
                            <TableCell numeric><Amount value={dept.totalNetSalary} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </section>
            </div>
          )}
        </ReportBody>
      )}

      {tab === 'attendance' && (
        <ReportBody data={rangeValid ? attendance.data : null} empty={!attendance.data?.summary.totalRecords} emptyText={rangeValid ? emptyText : t('pages:reports.attendance.dateRequired')} columns={5}>
          {attendance.data && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard title={t('pages:reports.attendance.totalRecords')} value={attendance.data.summary.totalRecords.toLocaleString(locale)} />
                <StatCard title={t('pages:reports.attendance.totalWorkHours')} value={hours(attendance.data.summary.totalWorkHours)} />
                <StatCard title={t('pages:reports.attendance.totalOvertimeHours')} value={hours(attendance.data.summary.totalOvertimeHours)} />
              </div>
              <section>
                <h2 className="text-base font-semibold text-fg mb-3">{t('pages:reports.attendance.employeeStats')}</h2>
                <div className={tableBox}>
                  <div className="overflow-x-auto">
                    <Table style={{ tableLayout: 'fixed', minWidth: '560px' }}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('pages:reports.attendance.employeeName')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.attendance.attendanceDays')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.attendance.leaveDays')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.attendance.absentDays')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.attendance.totalWorkHours')}</TableHead>
                          <TableHead numeric className="w-36">{t('translation:common.table.columns.overtimeHours')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attendance.data.employeeStats.map((stat: any) => (
                          <TableRow key={stat.employee.id}>
                            <TableCell className="font-medium text-fg">{stat.employee.name}</TableCell>
                            <TableCell numeric>{stat.presentDays}</TableCell>
                            <TableCell numeric>{stat.leaveDays}</TableCell>
                            <TableCell numeric>{stat.absentDays}</TableCell>
                            <TableCell numeric>{stat.totalWorkHours.toFixed(1)}</TableCell>
                            <TableCell numeric>{stat.totalOvertimeHours.toFixed(1)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </section>
            </div>
          )}
        </ReportBody>
      )}

      {tab === 'insurance' && (
        <ReportBody data={insurance.data} empty={!insurance.data?.summary.totalRecords} emptyText={emptyText} columns={3}>
          {insurance.data && (
            <div className="space-y-4">
              {/* 員工負擔／公司負擔對照表（原本藍、綠、主色三個色塊） */}
              <div className={tableBox}>
                <div className="overflow-x-auto">
                  <Table style={{ tableLayout: 'fixed', minWidth: '420px' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('pages:reports.insurance.item')}</TableHead>
                        <TableHead numeric className="w-36">{t('pages:reports.insurance.employeeBurden')}</TableHead>
                        <TableHead numeric className="w-36">{t('pages:reports.insurance.employerBurden')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(['LaborInsurance', 'HealthInsurance', 'LaborPension'] as const).map(item => (
                        <TableRow key={item}>
                          <TableCell>{t(`pages:reports.insurance.${item.charAt(0).toLowerCase() + item.slice(1)}Fee`)}</TableCell>
                          <TableCell numeric><Amount value={insurance.data.summary[`employee${item}`]} /></TableCell>
                          <TableCell numeric><Amount value={insurance.data.summary[`employer${item}`]} /></TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell className="font-medium text-fg">{t('pages:reports.insurance.subtotal')}</TableCell>
                        <TableCell numeric className="font-medium"><Amount value={insurance.data.summary.employeeTotal} /></TableCell>
                        <TableCell numeric className="font-medium"><Amount value={insurance.data.summary.employerTotal} /></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
              <p className="flex items-baseline justify-between rounded-lg border border-line bg-surface px-4 py-3">
                <span className="text-sm font-medium text-fg">{t('pages:reports.insurance.grandTotal')}</span>
                <span className="text-2xl font-semibold text-fg tabular-nums"><Amount value={insurance.data.summary.grandTotal} /></span>
              </p>
              <p className="text-[13px] text-fg-secondary">{t('pages:reports.insurance.estimateNote')}</p>
            </div>
          )}
        </ReportBody>
      )}

      {tab === 'cost' && (
        <ReportBody data={cost.data} empty={!cost.data?.monthlyData?.length} emptyText={costStartYear > costEndYear ? t('pages:reports.cost.yearOrder') : emptyText} columns={5}>
          {cost.data && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard title={t('pages:reports.cost.avgMonthlyCost')} value={<Amount value={Math.round(cost.data.summary.avgMonthlyCost)} />} hint={t('pages:reports.cost.avgHint', { count: cost.data.summary.totalMonths })} />
                <StatCard title={t('pages:reports.cost.avgEmployeeCount')} value={t('pages:reports.cost.peopleValue', { count: Math.round(cost.data.summary.avgEmployeeCount) })} />
              </div>
              {cost.data.monthlyData.length > 1 && (
                <section className="rounded-lg border border-line bg-surface p-5">
                  <h2 className="text-base font-semibold text-fg mb-4">{t('pages:reports.cost.monthlyTrend')}</h2>
                  <MonthlyBarChart
                    data={cost.data.monthlyData.map((m: any) => ({ month: m.period, value: m.totalCost }))}
                    formatValue={value => `$${Math.round(value).toLocaleString(locale)}`}
                    formatAxis={value => (value >= 10000 ? `${Math.round(value / 1000).toLocaleString(locale)}k` : String(value))}
                    formatMonth={month => month.replace('-', '/')}
                  />
                </section>
              )}
              <section>
                <h2 className="text-base font-semibold text-fg mb-3">{t('pages:reports.cost.monthlyDetails')}</h2>
                <div className={tableBox}>
                  <div className="overflow-x-auto">
                    <Table style={{ tableLayout: 'fixed', minWidth: '560px' }}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('pages:reports.cost.period')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.payroll.employeeCount')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.cost.totalSalary')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.cost.insuranceCost')}</TableHead>
                          <TableHead numeric className="w-36">{t('pages:reports.cost.totalCost')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cost.data.monthlyData.map((month: any) => (
                          <TableRow key={month.period}>
                            <TableCell className="tabular-nums">{month.period.replace('-', '/')}</TableCell>
                            <TableCell numeric>{month.employeeCount}</TableCell>
                            <TableCell numeric><Amount value={month.totalSalary} /></TableCell>
                            <TableCell numeric><Amount value={month.totalInsurance} /></TableCell>
                            <TableCell numeric className="font-medium"><Amount value={month.totalCost} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </section>
            </div>
          )}
        </ReportBody>
      )}
    </div>
  );
};

export default ReportPage;
